import { memo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { animated, useTransition } from '@react-spring/web';
import { ColorNeutralGrey3, ColorNeutralWhite, Spacing16, Spacing32 } from 'autogen/design-tokens/tokens';
import type { MenuRootViewModel } from 'autogen/swagger/all';
import { Menu as MenuTrans } from 'autogen/translation-keys/trans-website-shared';
import { Button } from 'components/Button';
import DrawerView from 'components/generic/Drawer/DrawerView';
import useDrawer from 'components/generic/Drawer/hooks/useDrawer';
import Menu from 'components/generic/menu';
import Layout from 'components/Layout';
import Flex from 'components/Layout/components/Flex';
import { hideOnMobile } from 'components/pages/Checkout/components/Delivery/shared/styles';
import QuickSearch from 'components/QuickSearch';
import MenuHeader from 'components/sideMenu/components/Menu/components/MenuHeader';
import isClient from 'helpers/isClient';
import { breakpoint, sizes, zIndex } from 'variables';
import useShouldFiltersBeSticky from '../ProductListFilter/shared/hooks/useShouldFiltersBeSticky';
import MenuItemsRenderer from './components/MenuItemsRenderer';

/* #region styles */
const StyledDrawerView = styled(DrawerView)`
    flex-direction: column;
    background-color: ${ColorNeutralWhite};
`;

const StyledDefaultMenuSection = styled(Menu.SecondarySection)`
    background-color: ${ColorNeutralWhite};
    &:after {
        width: 100%;
    }

    &:empty {
        display: none;
    }
`;

const StyledButton = styled(Button)`
    display: flex;
    width: 100%;
    ${breakpoint.up(sizes.lg)} {
        display: none;
    }
`;

const StickyHeader = styled<any>(animated.div)`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    padding: ${Spacing16} 0;
    background-color: ${ColorNeutralWhite};
    border-bottom: 1px solid ${ColorNeutralGrey3};
    z-index: ${zIndex.fixed};
`;
const StaticFilterContainer = styled<any>(animated.div)``;

const CustomContent = styled.div`
    display: grid;
    grid-template-columns: 1fr;
    gap: ${Spacing32};

    ${breakpoint.up(sizes.lg)} {
        grid-template-columns: 1fr 4fr;
    }
`;

const CategoryMenuContainer = styled(Flex.Column)``;

/* #endregion */

const CategorySideMenu = ({ model: { menuItems, text } }: { model: MenuRootViewModel }) => {
    const rootId = 'root-category-menu';

    const { open } = useDrawer(rootId);
    const { t } = useTranslation();
    const CategoryMenuContainerRef = useRef<HTMLDivElement>(null);
    const shouldCategoryMenuBeSticky = useShouldFiltersBeSticky(CategoryMenuContainerRef);

    const transition = useTransition(shouldCategoryMenuBeSticky, {
        from: (isSticky) => (isSticky ? { y: '-100%' } : { opacity: 0 }),
        enter: (isSticky) => (isSticky ? { y: '0%' } : { opacity: 1 }),
        leave: (isSticky) => (isSticky ? { y: '-100%' } : { opacity: 0 }),
        initial: (isSticky) => (isSticky ? undefined : false),
        expires: false
    });

    return (
        <CategoryMenuContainer ref={CategoryMenuContainerRef}>
            {/* This Transition Switches between the static and sticky when scrolled */}
            {transition((styles, isStickyFilters) =>
                isStickyFilters ? (
                    <StickyHeader style={{ ...styles }}>
                        <Layout.Section contentRenderer={(children) => <CustomContent>{children}</CustomContent>}>
                            <div css={hideOnMobile} />
                            <Flex.Column>
                                <QuickSearch shouldDisplayCameraButton={false} />
                                <StyledButton variant="secondary" onClick={open}>
                                    {t(MenuTrans)}
                                </StyledButton>
                            </Flex.Column>
                        </Layout.Section>
                    </StickyHeader>
                ) : (
                    <StaticFilterContainer style={isClient ? { ...styles } : { opacity: ' ' }}>
                        <StyledButton variant="secondary" onClick={open}>
                            {t(MenuTrans)}
                        </StyledButton>
                    </StaticFilterContainer> // react spring ssr fix
                )
            )}

            <StyledDrawerView id={rootId} position="left">
                <MenuHeader headline={text || t(MenuTrans)} styling="White" />
                <Menu>
                    <StyledDefaultMenuSection>
                        <MenuItemsRenderer menuItems={menuItems} />
                    </StyledDefaultMenuSection>
                </Menu>
            </StyledDrawerView>
        </CategoryMenuContainer>
    );
};

export default memo(CategorySideMenu);
